// src/pages/Admin/Products.js 
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../services/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  Tooltip,
  Fab,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { 
  Add, 
  Palette as PaletteIcon
} from '@mui/icons-material';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import ProductForm from '../../components/Common/ProductForm';
import CategoriesManager from '../../components/Common/CategoriesManager';
import ColorsManager from '../../components/Common/ColorsManager';
import ProductsFilter from '../../components/Common/ProductsFilter';
import ProductsTable from '../../components/Common/ProductsTable';

const AdminProducts = () => {
  const { isAdmin } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Main state
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProduct, setEditProduct] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openCategoriesDialog, setOpenCategoriesDialog] = useState(false);
  const [openColorsDialog, setOpenColorsDialog] = useState(false);
  const [categories, setCategories] = useState([]);
  const [colors, setColors] = useState([]);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const fetchProducts = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      setLoading(true);
      const { data } = await supabase
        .from('products')
        .select(`
          *,
          product_colors (
            id,
            color_id,
            color_order,
            is_home_color,
            colors(name),
            product_color_sizes ( id, size, stock ),
            product_color_images ( image_url, image_order )
          )
        `)
        .order('created_at', { ascending: false });

      // Process products to enhance with home color information
      const processedProducts = data?.map(product => {
        const sortedColors = product.product_colors?.sort((a, b) => a.color_order - b.color_order) || [];
        const homeColor = sortedColors.find(color => color.is_home_color) || sortedColors[0];
        
        return {
          ...product,
          product_colors: sortedColors,
          home_color: homeColor
        };
      }) || [];

      setProducts(processedProducts);
    } catch (error) {
      console.error('Products error:', error);
      showSnackbar('خطأ في تحميل المنتجات', 'error');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, showSnackbar]);

  const fetchCategories = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name', { ascending: true });
      
      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Categories error:', error);
    }
  }, []);

  const fetchColors = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('colors')
        .select('*')
        .order('name', { ascending: true });
      
      if (error) throw error;
      setColors(data || []);
    } catch (error) {
      console.error('Colors error:', error);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchProducts();
      fetchCategories();
      fetchColors();
    }
  }, [isAdmin, fetchProducts, fetchCategories, fetchColors]);

  // Helper functions
  const getTotalStock = (product) => {
    return product.product_colors?.reduce((total, color) => {
      return total + (color.product_color_sizes?.reduce((sizeTotal, size) => sizeTotal + size.stock, 0) || 0);
    }, 0) || 0;
  };

  const getStockStatus = (product) => {
    const totalStock = getTotalStock(product);
    if (totalStock === 0) {
      return { text: 'نفد المخزون', color: 'error' };
    } else if (totalStock <= 10) {
      return { text: 'مخزون منخفض', color: 'warning' };
    } else {
      return { text: 'متوفر', color: 'success' };
    }
  };

  // Helper function to check if product belongs to selected categories
  const isProductInCategory = (product, selectedMainCategoryId, selectedSubCategoryId, categories) => {
    const productCategory = categories.find(c => c.id === product.category_id);
    if (!productCategory) return false;
    
    // If subcategory is selected, check exact match
    if (selectedSubCategoryId) {
      return product.category_id === selectedSubCategoryId;
    }
    
    // If only main category is selected, check if product is in main category or its subcategories
    if (selectedMainCategoryId) {
      return product.category_id === selectedMainCategoryId || 
             productCategory.parent_id === selectedMainCategoryId;
    }
    
    // If no category filters are selected, show all products
    return true;
  };

  // Filter products based on search and filters
  const filteredProducts = products.filter(product => {
    // Search filter
    if (searchTerm && !product.name_ar.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Category filter
    if (selectedMainCategory || selectedSubCategory) {
      if (!isProductInCategory(product, selectedMainCategory, selectedSubCategory, categories)) {
        return false;
      }
    }

    // Stock filter
    if (stockFilter !== 'all') {
      const totalStock = getTotalStock(product);
      switch (stockFilter) {
        case 'in-stock':
          return totalStock > 10;
        case 'low-stock':
          return totalStock > 0 && totalStock <= 10;
        case 'out-of-stock':
          return totalStock === 0;
        default:
          return true;
      }
    }

    return true;
  });

  // Reset filters function
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedMainCategory('');
    setSelectedSubCategory('');
    setStockFilter('all');
  };

  const handleOpenEditDialog = (product) => {
    setEditProduct({
      id: product.id,
      name_ar: product.name_ar,
      description_ar: product.description_ar,
      price: product.price,
      new_price: product.new_price,
      category: product.category,
      category_id: product.category_id,
      colors: product.product_colors.map(pc => ({
        color: pc.color,
        color_id: pc.color_id,
        color_order: pc.color_order,
        is_home_color: pc.is_home_color,
        images: pc.product_color_images
          ?.sort((a, b) => (a.image_order || 0) - (b.image_order || 0))
          .map(img => img.image_url) || [],
        sizes: pc.product_color_sizes.map(s => ({ 
          size: s.size, 
          stock: s.stock
          // Removed price since it's now in the products table
        }))
      }))
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditProduct(null);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المنتج؟')) return;
    try {
      await supabase.from('products').delete().eq('id', id);
      setProducts(products.filter(p => p.id !== id));
      showSnackbar('تم حذف المنتج بنجاح');
    } catch (error) {
      console.error('Delete error:', error);
      showSnackbar('خطأ في حذف المنتج', 'error');
    }
  };

  if (!isAdmin) return <Typography>غير مصرح بالوصول</Typography>;
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: isMobile ? 1 : 2, pb: isMobile ? 10 : 2 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center',
        mb: 2,
        gap: 2
      }}>
        <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 'bold' }}>
          إدارة المنتجات ({filteredProducts.length})
        </Typography>
        
        {!isMobile && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title="إدارة الألوان">
              <Button 
                variant="outlined" 
                startIcon={<PaletteIcon />} 
                onClick={() => setOpenColorsDialog(true)}
                size="small"
                sx={{textTransform: 'none', fontWeight: 'bold', gap: 0.5 }}
              >
                الألوان
              </Button>
            </Tooltip>
            <Tooltip title="إدارة الفئات">
              <Button 
                variant="outlined" 
                startIcon={<CheckroomIcon />} 
                onClick={() => setOpenCategoriesDialog(true)}
                size="small"
                sx={{textTransform: 'none', fontWeight: 'bold', gap: 0.5 }}
              >
                الفئات
              </Button>
            </Tooltip>
            <Button 
              variant="contained" 
              startIcon={<Add />} 
              onClick={() => setOpenDialog(true)}
              sx={{textTransform: 'none', fontWeight: 'bold', gap: 0.5 }}
            >
              إضافة منتج
            </Button>
          </Box>
        )}
      </Box>

      {/* Filter Component */}
      <ProductsFilter
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedMainCategory={selectedMainCategory}
        setSelectedMainCategory={setSelectedMainCategory}
        selectedSubCategory={selectedSubCategory}
        setSelectedSubCategory={setSelectedSubCategory}
        stockFilter={stockFilter}
        setStockFilter={setStockFilter}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        categories={categories}
        filteredProductsCount={filteredProducts.length}
        onResetFilters={resetFilters}
      />

      {/* Products Table Component */}
      <ProductsTable
        products={filteredProducts}
        categories={categories}
        colors={colors}
        onEditProduct={handleOpenEditDialog}
        onDeleteProduct={handleDeleteProduct}
        getTotalStock={getTotalStock}
        getStockStatus={getStockStatus}
      />

      {/* Mobile FAB */}
      {isMobile && (
        <Box sx={{ 
          position: 'fixed', 
          bottom: 52, 
          right: 16, 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 1,
          zIndex: 1000
        }}>
          <Tooltip title="إضافة منتج" placement="left">
            <Fab 
              color="primary" 
              onClick={() => setOpenDialog(true)}
            >
              <Add />
            </Fab>
          </Tooltip>
          <Tooltip title="إدارة الفئات" placement="left">
            <Fab 
              color="secondary" 
              size="small"
              onClick={() => setOpenCategoriesDialog(true)}
            >
              <CheckroomIcon />
            </Fab>
          </Tooltip>
          <Tooltip title="إدارة الألوان" placement="left">
            <Fab 
              color="secondary" 
              size="small"
              onClick={() => setOpenColorsDialog(true)}
            >
              <PaletteIcon />
            </Fab>
          </Tooltip>
        </Box>
      )}

      {/* Dialogs */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        fullWidth 
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{ sx: { maxHeight: '90vh', overflow: 'hidden' } }}
      >
        <ProductForm
          product={editProduct}
          categories={categories}
          colors={colors}
          onClose={handleCloseDialog}
          onSuccess={() => {
            fetchProducts();
            handleCloseDialog();
          }}
        />
      </Dialog>

      <CategoriesManager 
        open={openCategoriesDialog} 
        onClose={() => {
          setOpenCategoriesDialog(false);
          fetchCategories();
        }}
      />

      <ColorsManager 
        open={openColorsDialog} 
        onClose={() => {
          setOpenColorsDialog(false);
          fetchColors();
        }}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminProducts;
